import React, { useRef, useEffect, useCallback } from 'react';
import { IonContent, IonMenu } from '@ionic/react';
import { useParams, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import MobileSidebar from '../components/organisms/MobileSidebar';
import { ToastProvider } from '../contexts/ToastContext';
import { SidebarProvider, useSidebar } from '../contexts/SidebarContext';
import './MainLayout.css';

const DRAG_COLLAPSE_THRESHOLD = 140;
const DRAG_EXPAND_THRESHOLD = 100;

const MainLayoutInner = ({ children, user, hideSidebar = false }) => {
  const pageRef = useRef(null);
  const { projectId } = useParams();
  const location = useLocation();
  // Full-viewport routes: the store DETAIL page's data views (SQL grid, docs
  // reader, KB search) fill the screen, so .main-container's width cap is
  // lifted for them — the innermost page re-caps narrow store types itself
  // (see StoragePage.css). Route-derived here because this cap lives ABOVE
  // PageLayout: no page-level class can reach it.
  const isFullBleedRoute = /\/stores\/[^/?#]+$/.test(location.pathname);
  const { isExpanded, setIsExpanded, toggleSidebar } = useSidebar();
  const dragRef = useRef(null);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const wasExpanded = isExpanded;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handle = e.currentTarget;
    handle.classList.add('sidebar-resize-handle--dragging');

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      if (wasExpanded && delta < -60) {
        if (moveEvent.clientX < DRAG_COLLAPSE_THRESHOLD) {
          setIsExpanded(false);
        }
      } else if (!wasExpanded && delta > 40) {
        if (moveEvent.clientX > DRAG_EXPAND_THRESHOLD) {
          setIsExpanded(true);
        }
      }
    };

    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      handle.classList.remove('sidebar-resize-handle--dragging');
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [isExpanded, setIsExpanded]);

  const isInsideProject = !!projectId;

  useEffect(() => {
    if (!isInsideProject) {
      setIsExpanded(true);
    }
  }, [isInsideProject, setIsExpanded]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        if (isInsideProject && !hideSidebar) {
          e.preventDefault();
          e.stopPropagation();
          toggleSidebar();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isInsideProject, hideSidebar, toggleSidebar]);

  return (
    <ToastProvider>
      {!hideSidebar && (
        <IonMenu 
          contentId="main-content" 
          menuId="sidebar-menu"
          side="start"
          type="overlay"
        >
          <MobileSidebar user={user} />
        </IonMenu>
      )}

      <IonContent id="main-content">
        {/* Fixed sidebar for project pages */}
        {isInsideProject && !hideSidebar && (
          <div className={`sidebar-fixed-wrapper ${!isExpanded ? 'sidebar-fixed-wrapper--collapsed' : ''}`}>
            <Sidebar user={user} isInsideProject />
            <div
              ref={dragRef}
              className="sidebar-resize-handle"
              onMouseDown={handleDragStart}
            />
          </div>
        )}

        <div className={`main-content-wrapper ${isInsideProject && !hideSidebar ? 'main-content-wrapper--with-sidebar' : ''} ${isInsideProject && !hideSidebar && !isExpanded ? 'main-content-wrapper--sidebar-collapsed' : ''}`}>
          <Header user={user} />
          <div className={`main-container${isFullBleedRoute ? ' main-container--fluid' : ''}`}>
            {/* Inline sidebar for non-project pages */}
            {!hideSidebar && !isInsideProject && (
              <div className="navigation-section">
                <Sidebar user={user} />
              </div>
            )}
            {React.cloneElement(children, { pageRef })}
          </div>
        </div>
      </IonContent>
    </ToastProvider>
  );
};

const MainLayout = (props) => {
  return (
    <SidebarProvider>
      <MainLayoutInner {...props} />
    </SidebarProvider>
  );
};

export default MainLayout;
